import {
  type AdDurationOption,
  type MaxAdDurationStatePayload,
  type VideoFormState,
  VideoOption,
} from '@openx/types/targeting/video.ts';
import { noop } from '@openx/utils/noop.ts';
import { type JSX, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionChipLine } from '../../../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle.tsx';
import { getMaxAdDurationOptions } from '../../utils/utils.ts';
import { MaxAdDurationSelectedItems } from './components/MaxAdDurationSelectedItems.tsx';
import { getDurationOptionLabel } from './utils/getDurationOptionLabel.ts';

type PrevStateCallback = (prevState: VideoFormState) => VideoFormState;

type Props = {
  readonly?: boolean;
  params: Partial<MaxAdDurationStatePayload>;
  shouldAddDimensionLine: boolean;
  onChange?: (newData: VideoFormState | PrevStateCallback) => void;
  disabledReasonMessage?: string;
};

export const MaxAdDurationItem = ({
  params,
  readonly = true,
  shouldAddDimensionLine,
  onChange = noop,
  disabledReasonMessage,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const adDurationOptions = useMemo(() => getMaxAdDurationOptions(t), [t]);
  const getItemLabel = useCallback((option: AdDurationOption) => getDurationOptionLabel(option, t), [t]);

  const handleChange = useCallback(() => {
    onChange(state => ({
      ...state,
      [VideoOption.MAX_AD_DURATION]: null,
    }));
  }, [onChange]);

  return (
    <>
      <FiltersTitle
        onClear={handleChange}
        title={t('Maximum Ad Duration')}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />
      <MaxAdDurationSelectedItems
        params={params}
        readonly={readonly}
        getItemLabel={getItemLabel}
        handleChange={handleChange}
        adDurationOptions={adDurationOptions}
      />
      {shouldAddDimensionLine && <DimensionChipLine />}
    </>
  );
};
